import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";

export default function PMWorkloadPopover({ wsId, pmName, pmUserId, fallbackProjects = [], fallbackOpen = 0, fallbackOverdue = 0, children }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !wsId || !pmUserId) return;
    setBusy(true);
    api.get(`/workspaces/${wsId}/pm-workload/${pmUserId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load workload"))
      .finally(() => setBusy(false));
  }, [open, wsId, pmUserId]);

  const projects = data?.projects || fallbackProjects.map((p) => ({
    id: p.id, name: p.name, client_name: p.client_name, status: p.status,
    counts: {
      open: (p.task_counts?.open || 0) + (p.task_counts?.in_progress || 0),
      overdue: p.milestone_flag?.overdue || 0,
      total: (p.task_counts?.total) || 0,
    },
  }));
  const totalOpen = data?.total_open ?? fallbackOpen;
  const totalOverdue = data?.total_overdue ?? fallbackOverdue;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-2 hover:bg-muted/40 px-1 py-0.5 -mx-1 -my-0.5"
          data-testid={`pm-workload-trigger-${pmUserId || pmName}`}>
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="rounded-none w-[420px] p-0">
        <div className="px-3 py-2 border-b border-border bg-muted/40 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs font-semibold truncate">{pmName || "Project Manager"}</div>
            <div className="text-[10px] text-muted-foreground">
              {projects.length} project{projects.length === 1 ? "" : "s"} ·
              <span className="font-mono tabular-nums"> {totalOpen}</span> open
              {totalOverdue > 0 && (
                <span className="text-rose-700"> · <span className="font-mono tabular-nums">{totalOverdue}</span> overdue</span>
              )}
            </div>
          </div>
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {projects.length === 0 && !busy && (
            <div className="p-4 text-center text-xs text-muted-foreground">No projects for this PM.</div>
          )}
          {projects.map((p) => {
            const openN = p.counts?.open || 0;
            const overdueN = p.counts?.overdue || 0;
            return (
              <a key={p.id}
                 href={`/app/projects/${p.id}`}
                 className="flex items-center gap-2 px-3 py-2 border-b border-border/60 hover:bg-muted/40"
                 data-testid={`pm-workload-project-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold truncate">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {p.client_name || "—"} · {(p.status || "").toLowerCase()}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-mono tabular-nums">
                    {openN}<span className="opacity-60"> open</span>
                  </div>
                  {overdueN > 0 && (
                    <div className="mt-0.5 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest px-1 border border-rose-500/50 bg-rose-500/10 text-rose-700">
                      <AlertTriangle className="w-2.5 h-2.5" /> {overdueN}
                    </div>
                  )}
                </div>
              </a>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
